import { generateUnderpaymentRecords, calculateRecoveryStats } from '@/lib/payerSimulation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DollarSign, TrendingUp, CheckCircle2, AlertCircle, Clock, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function FinancialRecoveryHeatmap() {
  const records = generateUnderpaymentRecords();
  const stats = calculateRecoveryStats(records);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'recovered':
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case 'auto-appealed':
        return <Loader2 className="w-4 h-4 text-primary animate-spin" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-warning" />;
      case 'detected':
        return <AlertCircle className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'recovered':
        return (
          <Badge className="bg-success/20 text-success border-success/30 gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Recovered
          </Badge>
        );
      case 'auto-appealed':
        return (
          <Badge className="bg-primary/20 text-primary border-primary/30 gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Auto-Appealed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-warning/20 text-warning border-warning/30 gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      case 'detected':
        return (
          <Badge className="bg-destructive/20 text-destructive border-destructive/30 gap-1">
            <AlertCircle className="w-3 h-3" />
            Detected
          </Badge>
        );
      default:
        return null;
    }
  };

  const getVarianceHeat = (percent: number) => {
    if (percent >= 15) return 'bg-destructive/20 text-destructive';
    if (percent >= 10) return 'bg-warning/20 text-warning';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="glass-card-elevated p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Financial Recovery Heatmap</h3>
        </div>
        <Badge variant="outline" className="gap-1 text-xs">
          Underpayment Detection Active
        </Badge>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-secondary/50 text-center">
          <p className="text-lg font-bold font-mono text-destructive">
            ${stats.totalVariance.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Total Underpaid</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/50 text-center">
          <p className="text-lg font-bold font-mono text-success">
            ${stats.totalRecovered.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Recovered</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/50 text-center">
          <p className="text-lg font-bold font-mono text-warning">
            ${stats.pendingRecovery.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/50 text-center">
          <p className="text-lg font-bold font-mono text-primary">
            {stats.recoveryRate.toFixed(0)}%
          </p>
          <p className="text-xs text-muted-foreground">Recovery Rate</p>
        </div>
      </div>

      {/* Underpayment Table */}
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs">Payer</TableHead>
            <TableHead className="text-xs">Claim ID</TableHead>
            <TableHead className="text-xs">CPT</TableHead>
            <TableHead className="text-xs text-right">Expected</TableHead>
            <TableHead className="text-xs text-right">Actual Paid</TableHead>
            <TableHead className="text-xs text-center">Variance</TableHead>
            <TableHead className="text-xs">Recovery Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => (
            <TooltipProvider key={record.id}>
              <TableRow>
                <TableCell className="font-medium text-sm">{record.payer}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {record.claimId}
                </TableCell>
                <TableCell className="font-mono text-sm">{record.cptCode}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  ${record.expectedRate.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  ${record.actualPaid.toLocaleString()}
                </TableCell>
                <TableCell className="text-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={cn(
                        "inline-flex items-center justify-center px-2 py-1 rounded font-mono font-bold text-xs",
                        getVarianceHeat(record.variancePercent)
                      )}>
                        -${record.variance.toLocaleString()} ({record.variancePercent.toFixed(1)}%)
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        Variance of {record.variancePercent.toFixed(1)}% below contractual rate
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {getStatusBadge(record.recoveryStatus)}
                </TableCell>
              </TableRow>
            </TooltipProvider>
          ))}
        </TableBody>
      </Table>

      {/* Auto-Recovery Summary */}
      <div className="mt-4 pt-4 border-t border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="text-sm">
                <span className="font-mono font-bold">{stats.recoveredCount}</span> claims recovered
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <span className="text-sm">
                <span className="font-mono font-bold">{stats.autoAppealedCount}</span> auto-appeals in progress
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-success">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">
              ${stats.totalRecovered.toLocaleString()} recovered this month
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
